import React, {useMemo} from "react"
import {
    extractAvailableSeasons,
    generateCountByKey,
    getDayOfYearLabel, getRegistryObject,
    getSeason, getSeasonLabel,
} from "../utils";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import Toolbar, {useToolbar} from "./Toolbar";

import {blue, green, red, orange, grey, purple, yellow, brown, cyan, lightBlue, lightGreen, deepPurple} from '@mui/material/colors';
import Box from "@mui/material/Box";


const categoryColorsMap = {
    "1": red[500],  // Skialp
    "3": blue[500],  // Off slope
    "4": cyan[500],  // In slope
    "5": green[500],  // Mountaineering
    "6": yellow[500],  // People on road
    "7": purple[500],  // People in building
    "8": grey[500],  // Other
    "9": brown[500],  // Hiking
    "10": orange[500], // Snowmobile
}

const subcategoryColorsMap = {
    // Skialp subcategories
    "1": red[500],
    "2": red[300],
    "101": red[400],
    "102": red[600],
    "103": red[700],
    "104": red[800],

    // Off slope subcategories
    "3": blue[500],
    "105": blue[300],
    "106": blue[400],
    "107": blue[600],

    // In slope subcategories
    "4": cyan[500],

    // Mountaineering subcategories
    "108": green[300],
    "109": green[400],
    "110": green[500],
    "111": green[600],

    // People on road subcategories
    "112": yellow[300],
    "113": yellow[400],
    "114": yellow[500],
    "115": yellow[600],
    "116": yellow[700],
    "117": yellow[800],
    "118": yellow[900],

    // People in building subcategories
    "119": purple[300],
    "120": purple[400],
    "121": purple[500],
    "122": purple[600],
    "123": purple[700],
    "124": purple[800],

    // Hiking subcategories
    "125": brown[300],
    "126": brown[400],
    "9": brown[500],
}

const dangerScaleColorsMap = {
    "1": green[500],
    "2": yellow[500],
    "3": orange[500],
    "4": red[500],
    "0": grey[500],
    "6": lightBlue[500],
}

const aspectColorsMap = {
    "0": grey[500],
    "2": blue[500],
    "3": cyan[500],
    "4": green[500],
    "5": lightGreen[500],
    "1": yellow[500],
    "6": orange[500],
    "7": purple[500],
    "8": deepPurple[500],
}


const IncidentSeasonCounts = ({data: originalData, registry}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();
    const height = 500;

    const categoryRegistry = getRegistryObject(registry, 'incidentCategoryV2');
    const subcategoryRegistry = getRegistryObject(registry, 'incidentSubcategory');
    const aspectRegistry = getRegistryObject(registry, 'aspect');
    const dangerScaleRegistry = getRegistryObject(registry, 'dangerScale');

    const availableAttributes = [
        {
            label: t('incidentSeasonCounts.legend.category'),
            value: 'category',
        },
        {
            label: t('incidentSeasonCounts.legend.subcategory'),
            value: 'subcategory',
        },
        {
            label: t('incidentSeasonCounts.legend.dangerScaleBulletin'),
            value: 'dangerScaleBulletin'
        },
        {
            label: t('incidentSeasonCounts.legend.aspect'),
            value: 'aspect',
        },
        {
            label: t('incidentSeasonCounts.legend.date'),
            value: 'date'
        }
    ]

    const data = useMemo(() => (originalData.map(({date, ...rest}) => {
            return {
                date,
                season: getSeason(date),
                dayOfYear: getDayOfYearLabel(date),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbar = useToolbar({availableSeasons, availableAttributes});
    const attribute = toolbar.selectedAttribute;

    // Get the data filtered by the toolbar selections
    const filteredData = useMemo( () => (
        data.filter( item => {
            const itemSeason = item.season;
            return (toolbar.seasonFrom ? itemSeason >= toolbar.seasonFrom : true)  && (toolbar.seasonTo ? itemSeason <= toolbar.seasonTo : true)
        })
    ), [data, toolbar.seasonFrom, toolbar.seasonTo]);
    const totalCount = filteredData.length;

    const categoryStat = generateCountByKey(filteredData, attribute === 'date' ? 'dayOfYear' : attribute);

    // based on the attribute, we might want to map the keys to more friendly labels
    let keys = Object.keys(categoryStat);

    let keyLabels;
    if (attribute === 'category') {
        keyLabels = keys.map(key => categoryRegistry[key] || key)
    } else if (attribute === 'subcategory') {
        keyLabels = keys.map(key => subcategoryRegistry[key] || key)
    } else if (attribute === 'dangerScaleBulletin') {
        keyLabels = keys.map(key => dangerScaleRegistry[key] || key)
    } else if (attribute === 'aspect') {
        keyLabels = keys.map(key => aspectRegistry[key] || key)
    }
    else {
        keyLabels = keys;
    }

    let colors = [];
    if (attribute === 'category') {
        colors = keys.map(key => categoryColorsMap[key] || grey[500]);
    } else if (attribute === 'subcategory') {
        colors = keys.map(key => subcategoryColorsMap[key] || grey[500]);
    } else if (attribute === 'dangerScaleBulletin') {
        colors = keys.map(key => dangerScaleColorsMap[key] || grey[500]);
    } else if (attribute === 'aspect') {
        colors = keys.map(key => aspectColorsMap[key] || grey[500]);
    }

    const subtitle = t('subtitle.seasonFrom', {seasonFrom: getSeasonLabel(toolbar.seasonFrom)}) + (toolbar.seasonTo ? t('subtitle.seasonTo', {seasonTo: getSeasonLabel(toolbar.seasonTo)}) : '');

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `incident_seasonal_counts`
        },
        title: {
            text: t('incidentSeasonCounts.title') + ': ' + t(`incidentSeasonCounts.legend.${attribute}`)
        },
        subtitle: {
            text: subtitle
        },
        series: [
            {
                data: keys.map((value, index) => ({
                    y: categoryStat[value] ?? 0,
                    color: colors[index] || undefined,
                })),
                type: 'column',
                name: t('incidentSeasonCounts.totalIncidents'),
                yAxis: 0,
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        if (totalCount === 0) {
                            return `${this.y}`;
                        } else{
                            return `${(this.y / totalCount * 100).toFixed(1)}%`;
                        }
                    }
                }
            },
        ],
        legend: {
            enabled: false
        },
        xAxis: {
            categories: keyLabels
        },
        yAxis: [
            {
                title: {
                    text: t('incidentSeasonCounts.numberOfIncidents')
                },
                allowDecimals: false,
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            },
            column: {
                dataLabels: {
                    enabled: true,
                    crop: false,
                    overflow: 'none',
                    formatter: function() {
                        return this.y;
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: height,
            ...chartLogoOptions,
        },
        ...creditsOptions,
    }

    const optionPie = {
        ...options,
        chart: {
            height: height,
            ...chartLogoOptions,
        },
        ...creditsOptions,
        legend: {
            enabled: true,
            align: 'left',
            verticalAlign: 'middle',
            layout: 'vertical'
        },

        series: [
            {
                type: 'pie',
                name: t('incidentSeasonCounts.totalIncidents'),
                showInLegend: true,

                data: keys.map((value, index) => ({
                    y: categoryStat[value] ?? 0,
                    color: colors[index] || undefined,
                    name: keyLabels[index], // <-- legend label
                })),

                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        if (totalCount === 0) {
                            return `${this.y}`;
                        } else {
                            return `${this.y} (${((this.y / totalCount) * 100).toFixed(1)}%)`;
                        }
                    }
                }
            }
        ]
    };
    return <>
        <Toolbar {...toolbar} />
        <Box height={height}>
            { toolbar.type === 'bar' &&  <HighchartsReact highcharts={Highcharts} options={options} /> }
            { toolbar.type === 'pie' && <HighchartsReact highcharts={Highcharts} options={optionPie}/> }
        </Box>
    </>
}

export default IncidentSeasonCounts